import type {
  PanderrNavigationItem,
  PanderrTemplateDefinition,
} from "../types";

export interface PanderrHeaderSiteData {
  businessName?: string;
  logoUrl?: string;
  navigation?: PanderrNavigationItem[];
}

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

const DEFAULT_NAVIGATION = [
  { label: "About", href: "/about/" },
  { label: "Services", href: "/services/" },
  { label: "Our Work", href: "/our-work/" },
  { label: "Contact", href: "/contact/" },
];

const DEFAULT_CTA = {
  label: "Get Started",
  href: "/contact/",
  variant: "primary" as const,
};

function renderNavigationItem(item: PanderrNavigationItem): string {
  const children = item.children || [];

  if (children.length === 0) {
    return `
      <a class="panderr-nav-link" href="${escapeHtml(item.href)}">
        ${escapeHtml(item.label)}
      </a>
    `;
  }

  return `
    <details class="panderr-nav-dropdown">
      <summary>${escapeHtml(item.label)}</summary>

      <div class="panderr-nav-dropdown-menu">
        ${
          item.href && item.href !== "#"
            ? `
              <a href="${escapeHtml(item.href)}">
                ${escapeHtml(item.label)} Overview
              </a>
            `
            : ""
        }

        ${children
          .map(
            (child) => `
              <a href="${escapeHtml(child.href)}">
                ${escapeHtml(child.label)}
              </a>
            `,
          )
          .join("")}
      </div>
    </details>
  `;
}

export function renderPanderrHeader(
  template: PanderrTemplateDefinition | undefined,
  siteData: PanderrHeaderSiteData = {},
): string {
  const navigation = siteData.navigation?.length
    ? siteData.navigation
    : template?.navigation?.length
      ? template.navigation
      : DEFAULT_NAVIGATION;

  const cta = template?.headerCta || DEFAULT_CTA;

  const businessName = siteData.businessName?.trim() || "Your Business";
  const logoUrl = siteData.logoUrl?.trim() || "";

  return `
    <header
      class="panderr-header"
      data-panderr-component="header"
      data-panderr-global-component="header"
    >
      <div class="panderr-shell-wide panderr-header-inner">
        <a
          class="panderr-brand"
          href="/"
          aria-label="Home"
          data-panderr-role="logo-slot"
          data-panderr-component="logo-slot"
        >
          ${
            logoUrl
              ? `<img
                  class="panderr-site-logo"
                  src="${escapeHtml(logoUrl)}"
                  alt="${escapeHtml(businessName)}"
                  data-panderr-site-logo="true"
                  data-panderr-role="logo"
                  data-panderr-component="logo"
                >`
              : `<span
                  class="panderr-text-brand"
                  data-panderr-role="text-brand"
                  data-panderr-component="text-brand"
                >${escapeHtml(businessName)}</span>`
          }
        </a>

<nav class="panderr-nav" aria-label="Primary navigation">
  ${navigation.map(renderNavigationItem).join("")}
</nav>

        <a
          class="panderr-button panderr-button-primary panderr-header-cta"
          href="${escapeHtml(cta.href)}"
        >
          ${escapeHtml(cta.label)}
        </a>
      </div>
    </header>
  `;
}

export function createHeaderCss(): string {
  return `
    .panderr-header {
      position: relative;
      z-index: 20;
      border-bottom: 1px solid var(--panderr-color-border);
      background: rgba(255,255,255,.96);
    }

    .panderr-header-inner {
      min-height: 84px;
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 2rem;
      align-items: center;
    }

    .panderr-brand {
      display: inline-flex;
      align-items: center;
      gap: .8rem;
      width: fit-content;
      max-width: 100%;
      font-weight: 800;
    }

    .panderr-site-logo {
      display: block;
      width: auto;
      max-width: 190px;
      max-height: 58px;
      object-fit: contain;
      object-position: left center;
    }

    .panderr-text-brand {
      display: block;
      color: var(--panderr-color-text);
      font-family: var(--panderr-heading-font);
      font-size: 1.15rem;
      font-weight: 800;
      line-height: 1.1;
      text-decoration: none;
    }

    .panderr-nav {
      display: flex;
      gap: clamp(1rem, 1.7vw, 2rem);
      align-items: center;
      justify-content: center;
      font-size: .95rem;
      font-weight: 600;
    }

    .panderr-nav a {
      color: var(--panderr-color-text-muted);
      transition: color var(--panderr-transition-fast);
      white-space: nowrap;
    }

    .panderr-nav a:hover {
      color: var(--panderr-color-text);
    }

        .panderr-nav-dropdown {
      position: relative;
    }

    .panderr-nav-dropdown summary {
      display: flex;
      align-items: center;
      gap: .4rem;
      color: var(--panderr-color-text-muted);
      cursor: pointer;
      list-style: none;
      transition: color var(--panderr-transition-fast);
      white-space: nowrap;
    }

    .panderr-nav-dropdown summary::-webkit-details-marker {
      display: none;
    }

    .panderr-nav-dropdown summary::after {
      content: "";
      width: .45rem;
      height: .45rem;
      border-right: 1.5px solid currentColor;
      border-bottom: 1.5px solid currentColor;
      transform: translateY(-.15rem) rotate(45deg);
      transition: transform var(--panderr-transition-fast);
    }

    .panderr-nav-dropdown[open] summary,
    .panderr-nav-dropdown summary:hover {
      color: var(--panderr-color-text);
    }

    .panderr-nav-dropdown[open] summary::after {
      transform: translateY(.1rem) rotate(225deg);
    }

    .panderr-nav-dropdown-menu {
      position: absolute;
      z-index: 30;
      top: calc(100% + .8rem);
      left: 50%;
      display: grid;
      min-width: 220px;
      padding: .65rem;
      border: 1px solid var(--panderr-color-border);
      border-radius: 14px;
      background: #fff;
      box-shadow: 0 18px 48px rgba(20, 20, 20, .14);
      transform: translateX(-50%);
    }

    .panderr-nav-dropdown-menu a {
      display: block;
      padding: .75rem .85rem;
      border-radius: 9px;
    }

    .panderr-nav-dropdown-menu a:hover,
    .panderr-nav-dropdown-menu a:focus-visible {
      background: var(--panderr-color-surface);
    }

    .panderr-header-cta {
      justify-self: end;
    }

    @media (max-width: 1040px) {
      .panderr-nav {
        gap: 1rem;
        font-size: .88rem;
      }
    }

    @media (max-width: 820px) {
      .panderr-header-inner {
        grid-template-columns: 1fr auto;
      }

      .panderr-nav {
        display: none;
      }
    }

    @media (max-width: 520px) {
      .panderr-site-logo {
        max-width: 150px;
        max-height: 50px;
      }
    }
  `;
}
